import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { alias } from '@ember/object/computed';
import Controller, { inject as controller } from '@ember/controller';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { CampaignType } from 'cio/models/campaign';

export default class ListController extends Controller {
  @service router;
  @service store;
  @service analytics;

  @tracked searchString = this.search;

  @controller('broadcasts.list.newsletters.index') newslettersList;

  @controller('broadcasts.list.api-triggered.index') apiTriggeredList;

  // eslint-disable-next-line getter-return
  @computed('router.currentRouteName', 'newslettersList', 'apiTriggeredList')
  get _activeChildRoute() {
    if (this.router.isActive('broadcasts.list.api-triggered')) {
      return this.apiTriggeredList;
    } else if (this.router.isActive('broadcasts.list.newsletters')) {
      return this.newslettersList;
    }
  }

  @alias('_activeChildRoute.page') page;

  @alias('_activeChildRoute.search') search;

  @alias('_activeChildRoute.endTime') endTime;

  @alias('_activeChildRoute.startTime') startTime;

  @alias('_activeChildRoute.channel') channel;

  @alias('_activeChildRoute.total') total;

  @alias('_activeChildRoute.broadcasts') broadcasts;

  _itemsPerPage = 20;

  @computed('router.currentRouteName')
  get _isNewslettersTabActive() {
    return this.router.isActive('broadcasts.list.newsletters');
  }

  @computed('router.currentRouteName')
  get _isApiTriggeredBroadcastsTabActive() {
    return this.router.isActive('broadcasts.list.api-triggered');
  }

  // When a child route (newsletters or api-triggered) is transitioned into, it
  // calls the `transitionedIntoChildRoute` function which sets the
  // `activeChildRouteName` value so that we can restore the last active child
  // route when needed.
  lastActiveChildRouteName = null;

  transitionedIntoChildRoute(routeName) {
    this.set('lastActiveChildRouteName', routeName);
  }

  // Fetches counts for both the newsletters and api triggered broadcasts tabs.
  // Both the child routes call this in their `setupController` hook.
  @action
  fetchCounts() {
    this.fetchNewsletterCounts.perform();
    this.fetchAPITBCounts.perform();
  }

  fetchNewsletterCounts = task({ drop: true }, async () => {
    if (this._isNewslettersTabActive) {
      // When a tab is loaded, we already have its model, so we avoid making an
      // extra request to load it again.
      this.newslettersCount = (this.broadcasts ?? []).meta.pagination.total;
    } else {
      // The newsletters endpoint sends us limited records at a time (20) and the
      // total count is in the meta
      let newsletters = await this.store.query('newsletter', { page: 1, search: this.search });
      this.newslettersCount = newsletters.meta.pagination.total;
    }
  });

  fetchAPITBCounts = task({ drop: true }, async () => {
    if (this._isApiTriggeredBroadcastsTabActive) {
      // When a tab is loaded, we already have its model, so we avoid making an
      // extra request to load it again.
      this.apiTriggeredBroadcastsCount = (this.broadcasts ?? []).meta.pagination.total;
    } else {
      // The campaigns endpoint sends us limited records at a time (20) and the
      // total count is in the meta
      let apiTriggeredBroadcasts = await this.store.query('campaign', {
        type: CampaignType.APITriggered,
        page: 1,
        search: this.search,
      });
      this.apiTriggeredBroadcastsCount = apiTriggeredBroadcasts.meta.pagination.total;
    }
  });

  get hasNewslettersCount() {
    return this.newslettersCount !== null;
  }

  get hasAPITBsCount() {
    return this.apiTriggeredBroadcastsCount !== null;
  }

  @tracked newslettersCount = null;
  @tracked apiTriggeredBroadcastsCount = null;

  @action
  searchFor(ev) {
    ev.preventDefault();
    this.setProperties({
      page: 1,
      search: this.searchString,
    });
  }

  @action
  setSearch(value) {
    this.searchString = value;
  }

  @action
  clearSearch() {
    this.setProperties({
      page: 1,
      search: '',
    });
  }

  async callAnalyticsForList() {
    let tags = this.store.peekAll('tag');

    if (tags.toArray().length === 0) {
      tags = await this.store.findAll('tag');
    }

    this.analytics.track('broadcast', 'list_viewed', {
      objectTypes: this.store.peekAll('object-type'),
      tags,
    });
  }
}
